/**
 * Diaels Collection Filters JS
 * Manages shape selector, metal & shape popovers, mobile drawer, and filtering
 */

class DiaelsCollectionFilters {
  constructor() {
    this.activeFilters = {
      shape: null,
      metal: null,
      carat: null,
      style: null,
      sort: 'manual'
    };

    this.container = document.getElementById('CollectionFiltersContainer');
    if (!this.container) return;

    this.readUrlParams();
    this.initEventListeners();
    this.applyFilters();
  }

  readUrlParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('shape')) this.activeFilters.shape = params.get('shape');
    if (params.get('metal')) this.activeFilters.metal = params.get('metal');
    if (params.get('carat')) this.activeFilters.carat = params.get('carat');
    if (params.get('style')) this.activeFilters.style = params.get('style');
    if (params.get('sort_by')) this.activeFilters.sort = params.get('sort_by');
  }

  updateUrl() {
    const params = new URLSearchParams(window.location.search);
    ['shape', 'metal', 'carat', 'style'].forEach(key => {
      if (this.activeFilters[key]) {
        params.set(key, this.activeFilters[key]);
      } else {
        params.delete(key);
      }
    });

    if (this.activeFilters.sort && this.activeFilters.sort !== 'manual') {
      params.set('sort_by', this.activeFilters.sort);
    } else {
      params.delete('sort_by');
    }

    const query = params.toString();
    const newUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }

  initEventListeners() {
    // 1. Desktop Dropdown Triggers
    document.querySelectorAll('.js-drop-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const wrapper = trigger.closest('.viewer3d-dropdown-wrapper');
        const content = wrapper ? wrapper.querySelector('.viewer3d-dropdown-content') : null;
        if (!content) return;

        const isCurrentlyOpen = content.classList.contains('show');

        // Close all other dropdowns
        document.querySelectorAll('.viewer3d-dropdown-content').forEach(el => el.classList.remove('show'));
        document.querySelectorAll('.js-drop-trigger').forEach(el => {
          el.classList.remove('is-open');
          el.setAttribute('aria-expanded', 'false');
        });

        if (!isCurrentlyOpen) {
          content.classList.add('show');
          trigger.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Close desktop dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.viewer3d-dropdown-wrapper')) {
        document.querySelectorAll('.viewer3d-dropdown-content').forEach(el => el.classList.remove('show'));
        document.querySelectorAll('.js-drop-trigger').forEach(el => {
          el.classList.remove('is-open');
          el.setAttribute('aria-expanded', 'false');
        });
      }
    });

    // 2. Shape Buttons (Top Row + Drawer + Popover)
    document.querySelectorAll('.js-shape-filter-btn, .js-shape-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const shape = btn.dataset.shape;
        if (this.activeFilters.shape === shape) {
          this.activeFilters.shape = null; // Toggle off
        } else {
          this.activeFilters.shape = shape;
        }
        this.syncUI();
        this.applyFilters();
      });
    });

    // 3. Metal Items
    document.querySelectorAll('.js-metal-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const metal = item.dataset.metal;
        if (this.activeFilters.metal === metal) {
          this.activeFilters.metal = null; // Toggle off
        } else {
          this.activeFilters.metal = metal;
        }
        this.syncUI();
        this.applyFilters();
      });
    });

    // 4. Carat Items
    document.querySelectorAll('.js-carat-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const carat = item.dataset.carat;
        if (this.activeFilters.carat === carat) {
          this.activeFilters.carat = null; // Toggle off
        } else {
          this.activeFilters.carat = carat;
        }
        this.syncUI();
        this.applyFilters();
      });
    });

    // 5. Style Items
    document.querySelectorAll('.js-style-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const style = item.dataset.style;
        if (this.activeFilters.style === style) {
          this.activeFilters.style = null; // Toggle off
        } else {
          this.activeFilters.style = style;
        }
        this.syncUI();
        this.applyFilters();
      });
    });

    // 6. Sort Select
    document.querySelectorAll('.js-sort-select').forEach(select => {
      select.addEventListener('change', (e) => {
        this.activeFilters.sort = e.target.value;
        // Sync desktop & mobile selects
        document.querySelectorAll('.js-sort-select').forEach(s => s.value = e.target.value);
        this.applyFilters();
      });
    });

    // 7. Mobile Drawer Controls
    const drawer = document.getElementById('Viewer3dDrawer');
    const overlay = document.querySelector('.viewer3d-mobile-overlay');

    document.querySelectorAll('.js-mobile-open').forEach(btn => {
      btn.addEventListener('click', () => {
        if (drawer) drawer.classList.add('is-active');
        if (overlay) overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });
    });

    document.querySelectorAll('.js-mobile-close').forEach(btn => {
      btn.addEventListener('click', () => {
        if (drawer) drawer.classList.remove('is-active');
        if (overlay) overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      });
    });

    // 8. Mobile Drawer Accordions
    document.querySelectorAll('.js-acc-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const panel = trigger.nextElementSibling;
        const isOpen = panel && panel.classList.contains('show');
        if (panel) {
          if (isOpen) {
            panel.classList.remove('show');
            trigger.classList.remove('is-open');
          } else {
            panel.classList.add('show');
            trigger.classList.add('is-open');
          }
        }
      });
    });
  }

  syncUI() {
    // 1. Sync Shapes
    document.querySelectorAll('.js-shape-filter-btn, .js-shape-item').forEach(el => {
      if (this.activeFilters.shape && el.dataset.shape.toLowerCase() === this.activeFilters.shape.toLowerCase()) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });

    // 2. Sync Metals
    document.querySelectorAll('.js-metal-item').forEach(el => {
      if (this.activeFilters.metal && el.dataset.metal.toLowerCase() === this.activeFilters.metal.toLowerCase()) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });

    // 3. Sync Carats
    document.querySelectorAll('.js-carat-item').forEach(el => {
      if (this.activeFilters.carat && el.dataset.carat === this.activeFilters.carat) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });

    // 4. Sync Styles
    document.querySelectorAll('.js-style-item').forEach(el => {
      if (this.activeFilters.style && el.dataset.style.toLowerCase() === this.activeFilters.style.toLowerCase()) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    });

    // 5. Update Trigger Labels / Indicators
    const shapeTrigger = document.querySelector('.viewer3d-dropdown-wrapper[data-dropdown="shape"] .viewer3d-trigger-text');
    if (shapeTrigger) {
      shapeTrigger.textContent = this.activeFilters.shape ? `Shape: ${this.activeFilters.shape}` : 'Diamond Shape';
    }

    const metalTrigger = document.querySelector('.viewer3d-dropdown-wrapper[data-dropdown="metal"] .viewer3d-trigger-text');
    if (metalTrigger) {
      metalTrigger.textContent = this.activeFilters.metal ? `Metal: ${this.activeFilters.metal}` : 'Metal Type';
    }

    const caratTrigger = document.querySelector('.viewer3d-dropdown-wrapper[data-dropdown="carat"] .viewer3d-trigger-text');
    if (caratTrigger) {
      caratTrigger.textContent = this.activeFilters.carat ? `Carat: ${this.activeFilters.carat} CT` : 'Carat Weight';
    }

    const styleTrigger = document.querySelector('.viewer3d-dropdown-wrapper[data-dropdown="style"] .viewer3d-trigger-text');
    if (styleTrigger) {
      styleTrigger.textContent = this.activeFilters.style ? `Style: ${this.activeFilters.style}` : 'Shape';
    }

    // 6. Update Active Pills Container
    this.renderActivePills();

    // 7. Update Filter Counts
    const activeCount = ['shape', 'metal', 'carat', 'style'].filter(k => this.activeFilters[k]).length;
    document.querySelectorAll('.js-filter-count-badge').forEach(badge => {
      badge.textContent = activeCount;
      if (activeCount > 0) {
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    });
    document.querySelectorAll('.js-filter-count-text').forEach(el => {
      el.textContent = activeCount;
    });
  }

  renderActivePills() {
    const container = document.getElementById('Viewer3dActiveContainer');
    if (!container) return;

    let html = '';
    const activeKeys = ['shape', 'metal', 'carat', 'style'].filter(k => this.activeFilters[k]);

    if (activeKeys.length > 0) {
      activeKeys.forEach(k => {
        const val = this.activeFilters[k];
        const label = k.charAt(0).toUpperCase() + k.slice(1);
        html += `
          <div class="viewer3d-pill" data-key="${k}">
            <span>${label}: <strong>${val}</strong></span>
            <span class="viewer3d-pill-close js-remove-pill" data-key="${k}">&times;</span>
          </div>
        `;
      });

      html += `<button type="button" class="viewer3d-clear-all js-clear-all">Clear All</button>`;
    }

    container.innerHTML = html;

    // Remove pill event listener
    container.querySelectorAll('.js-remove-pill').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const key = btn.dataset.key;
        this.activeFilters[key] = null;
        this.syncUI();
        this.applyFilters();
      });
    });

    // Clear all event listener
    const clearBtn = container.querySelector('.js-clear-all');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.activeFilters.shape = null;
        this.activeFilters.metal = null;
        this.activeFilters.carat = null;
        this.activeFilters.style = null;
        this.syncUI();
        this.applyFilters();
      });
    }
  }

  applyFilters() {
    this.updateUrl();

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const cards = productGrid.querySelectorAll('.grid__item, .product-grid-item, li');
    let visibleCount = 0;

    cards.forEach(card => {
      // Don't filter empty state elements
      if (card.classList.contains('collection--empty')) return;

      const title = (card.querySelector('.card__heading, .product-card__title, .premium-product-card__title')?.textContent || '').toLowerCase();
      const cardText = card.textContent.toLowerCase();

      let matchShape = true;
      let matchMetal = true;
      let matchCarat = true;
      let matchStyle = true;

      // 1. Shape check
      if (this.activeFilters.shape) {
        const s = this.activeFilters.shape.toLowerCase();
        matchShape = title.includes(s) || cardText.includes(s);
      }

      // 2. Metal check
      if (this.activeFilters.metal) {
        const m = this.activeFilters.metal.toLowerCase();
        if (m.includes('rose')) {
          matchMetal = cardText.includes('rose');
        } else if (m.includes('white')) {
          matchMetal = cardText.includes('white') || cardText.includes('silver');
        } else if (m.includes('yellow')) {
          matchMetal = cardText.includes('yellow') || (cardText.includes('gold') && !cardText.includes('rose') && !cardText.includes('white'));
        } else if (m.includes('platinum')) {
          matchMetal = cardText.includes('platinum');
        }
      }

      // 3. Carat check
      if (this.activeFilters.carat) {
        const c = this.activeFilters.carat;
        const cShort = parseFloat(c).toString(); // e.g. "0.5"
        matchCarat = title.includes(c) || title.includes(cShort) || cardText.includes(c) || cardText.includes(cShort);
      }

      // 4. Style check
      if (this.activeFilters.style) {
        const st = this.activeFilters.style.toLowerCase();
        matchStyle = title.includes(st) || cardText.includes(st);
      }

      if (matchShape && matchMetal && matchCarat && matchStyle) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    // Handle Empty Search/Filter Results
    let emptyMsg = productGrid.querySelector('.diaels-empty-filter-state');
    if (visibleCount === 0 && cards.length > 0) {
      if (!emptyMsg) {
        emptyMsg = document.createElement('div');
        emptyMsg.className = 'diaels-empty-filter-state';
        emptyMsg.style.cssText = 'grid-column: 1/-1; text-align: center; padding: 60px 20px;';
        emptyMsg.innerHTML = `
          <h3 style="font-size: 20px; font-weight: 600; margin-bottom: 12px; color: #111;">No products found</h3>
          <p style="font-size: 14px; color: #666; margin-bottom: 20px;">Try adjusting your selected filters to find what you're looking for.</p>
          <button type="button" class="button button--secondary js-empty-clear" style="padding: 10px 24px; cursor: pointer;">Clear all filters</button>
        `;
        productGrid.appendChild(emptyMsg);
        emptyMsg.querySelector('.js-empty-clear').addEventListener('click', () => {
          this.activeFilters.shape = null;
          this.activeFilters.metal = null;
          this.activeFilters.carat = null;
          this.activeFilters.style = null;
          this.syncUI();
          this.applyFilters();
        });
      }
      emptyMsg.style.display = 'block';
    } else if (emptyMsg) {
      emptyMsg.style.display = 'none';
    }

    // Sort products if requested
    if (this.activeFilters.sort && this.activeFilters.sort !== 'manual') {
      this.sortProductCards(productGrid, this.activeFilters.sort);
    }
  }

  sortProductCards(grid, sortKey) {
    const cards = Array.from(grid.children).filter(el => !el.classList.contains('diaels-empty-filter-state'));
    cards.sort((a, b) => {
      const getPrice = (el) => {
        const priceEl = el.querySelector('.price-item--regular, .price, .premium-product-card__price');
        if (!priceEl) return 0;
        const num = priceEl.textContent.replace(/[^0-9.]/g, '');
        return parseFloat(num) || 0;
      };

      const getTitle = (el) => {
        return (el.querySelector('.card__heading, .product-card__title, .premium-product-card__title')?.textContent || '').trim().toLowerCase();
      };

      if (sortKey === 'price-ascending') {
        return getPrice(a) - getPrice(b);
      } else if (sortKey === 'price-descending') {
        return getPrice(b) - getPrice(a);
      } else if (sortKey === 'title-ascending') {
        return getTitle(a).localeCompare(getTitle(b));
      } else if (sortKey === 'title-descending') {
        return getTitle(b).localeCompare(getTitle(a));
      }
      return 0;
    });

    cards.forEach(card => grid.appendChild(card));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.diaelsFilters = new DiaelsCollectionFilters();
});
